// Η κάτοψη ως χώρος στον editor του ταμείου: περιοχές με τοίχους (ή ανοιχτή αυλή),
// πόρτες/παράθυρα πάνω στους τοίχους, σταθερά στοιχεία, σε μονάδες χώρου (50 = 1 μ.).
// Ίδια σχεδίαση με τη Sala (xentrix_table src/ui/floor/Venue.tsx), με τα χρώματα του ταμείου.

import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import { fit, type Bounds, type Fixture, type Opening, type RoomKind } from '../../../utils/salaFloor/floor';

export interface VenueRoom { key: string | number; name: string; kind: RoomKind; x: number; y: number; w: number; h: number; openings: Opening[] }

/** Χρώματα από το θέμα του ταμείου: σωστά σε ανοιχτό και σκούρο. */
export const C = {
  wall: 'hsl(var(--text-3))',
  room: 'hsl(var(--surface-1))',
  court: 'hsl(var(--surface-2))',
  outside: 'hsl(var(--surface-0))',
  line: 'hsl(var(--line))',
  lineStrong: 'hsl(var(--line-strong))',
  focus: '#3b82f6',
  error: '#dc2626',
  warn: '#d97706',
};

const ScaleCtx = createContext(1);
/** Πόσα px της οθόνης είναι μία μονάδα χώρου. */
export const useVenueScale = () => useContext(ScaleCtx);

/** Αριθμός + μία γραμμή μέσα σε τραπέζι, σε μέγεθος ΟΘΟΝΗΣ (διαβάζεται ίδιο σε κάθε ζουμ). */
export function TableFace({ n, w, h, sub }: { n: string; w: number; h: number; sub?: React.ReactNode }) {
  const s = useVenueScale();
  const px = Math.min(w, h) * s;
  const num = Math.max(13, Math.min(24, px * 0.36));
  return (
    <>
      <span className="font-bold leading-none" style={{ fontSize: num / s }}>{n}</span>
      {sub && px >= 52 && <span className="inline-flex items-center gap-[3px] whitespace-nowrap leading-tight" style={{ fontSize: 12 / s }}>{sub}</span>}
    </>
  );
}

/** Μετρά το κουτί του και κλιμακώνει τον χώρο ώστε να χωρούν τα `bounds`. */
export function VenueViewport({ bounds, pad = [24, 18, 24, 18], maxScale = 1.6, className = '', children, overlay, underlay, scaleRef }: {
  bounds: Bounds; pad?: [number, number, number, number]; maxScale?: number; className?: string;
  children: React.ReactNode; overlay?: React.ReactNode; underlay?: React.ReactNode; scaleRef?: { current: number };
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const [pl, pt, pr, pb] = pad;
  const v = size && size.w > 0 ? fit(bounds, size.w - pl - pr, size.h - pt - pb, maxScale) : null;
  if (scaleRef && v) scaleRef.current = v.s;
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      {underlay}
      {v && (
        <div className="absolute left-0 top-0 h-px w-px origin-top-left transition-transform duration-300 ease-out"
          style={{ transform: `translate(${v.tx + pl}px, ${v.ty + pt}px) scale(${v.s})` }}>
          <ScaleCtx.Provider value={v.s}>{children}</ScaleCtx.Provider>
        </div>
      )}
      {overlay}
    </div>
  );
}

const WALL = 6;

/** Πού και πώς σχεδιάζεται ένα άνοιγμα (σε μονάδες χώρου). Ίδιο με τη Sala. */
export function openingParts(r: { x: number; y: number; w: number; h: number; kind: RoomKind }, o: Opening) {
  const W = r.kind === 'indoor' ? WALL : 2;
  const L = o.len;
  const top = o.side === 'top';
  const left = o.side === 'left';
  const horiz = o.side === 'top' || o.side === 'bottom';
  const gap = o.type === 'window' ? null : {
    left: horiz ? r.x + o.at : left ? r.x - 2 : r.x + r.w - W - 2,
    top: horiz ? (top ? r.y - 2 : r.y + r.h - W - 2) : r.y + o.at,
    width: horiz ? L : W + 4,
    height: horiz ? W + 4 : L,
  };
  const strip = o.type === 'window' && r.kind === 'indoor'
    ? horiz
      ? { left: r.x + o.at, top: top ? r.y : r.y + r.h - W, width: L, height: W, horiz: true }
      : { left: left ? r.x : r.x + r.w - W, top: r.y + o.at, width: W, height: L, horiz: false }
    : null;
  const leaves: { left: number; top: number; s: number; hinge: 'l' | 'r' | 't' | 'b'; open: 'down' | 'up' | 'right' | 'left' }[] = [];
  if (o.type !== 'window' && r.kind === 'indoor') {
    const double = o.type === 'entrance';
    const s = Math.min(46, double ? L / 2 : L);
    const a0 = (horiz ? r.x : r.y) + o.at;
    const a1 = a0 + L;
    if (horiz) {
      const t = top ? r.y + W : r.y + r.h - W - s;
      leaves.push({ left: a0, top: t, s, hinge: 'l', open: top ? 'down' : 'up' });
      if (double) leaves.push({ left: a1 - s, top: t, s, hinge: 'r', open: top ? 'down' : 'up' });
    } else {
      const l = left ? r.x + W : r.x + r.w - W - s;
      leaves.push({ left: l, top: a0, s, hinge: 't', open: left ? 'right' : 'left' });
      if (double) leaves.push({ left: l, top: a1 - s, s, hinge: 'b', open: left ? 'right' : 'left' });
    }
  }
  const mid = (horiz ? r.x : r.y) + o.at + L / 2;
  const label = o.label
    ? horiz
      ? { left: mid, top: top ? r.y - 22 : r.y + r.h + 6, vertical: false }
      : { left: left ? r.x - 20 : r.x + r.w + 8, top: mid, vertical: true }
    : null;
  return { gap, strip, leaves, label };
}

function leafStyle(l: ReturnType<typeof openingParts>['leaves'][number]): React.CSSProperties {
  const solid = `2px solid ${C.wall}`;
  const arc = `1.5px dashed ${C.lineStrong}`;
  const st: React.CSSProperties = { left: l.left, top: l.top, width: l.s, height: l.s };
  const r = `${l.s}px`;
  if (l.hinge === 'l' || l.hinge === 'r') {
    st.borderLeft = l.hinge === 'l' ? solid : arc;
    st.borderRight = l.hinge === 'r' ? solid : arc;
    if (l.open === 'down') { st.borderBottom = arc; st.borderRadius = l.hinge === 'l' ? `0 0 ${r} 0` : `0 0 0 ${r}`; }
    else { st.borderTop = arc; st.borderRadius = l.hinge === 'l' ? `0 ${r} 0 0` : `${r} 0 0 0`; }
  } else {
    st.borderTop = l.hinge === 't' ? solid : arc;
    st.borderBottom = l.hinge === 'b' ? solid : arc;
    if (l.open === 'right') { st.borderRight = arc; st.borderRadius = l.hinge === 't' ? `0 0 ${r} 0` : `0 ${r} 0 0`; }
    else { st.borderLeft = arc; st.borderRadius = l.hinge === 't' ? `0 0 0 ${r}` : `${r} 0 0 0`; }
  }
  return st;
}

/** Μία περιοχή: δάπεδο με τοίχους (ή ανοιχτή αυλή), ανοίγματα, προαιρετικά τίτλος. */
export function RoomShape({ room, label, grid, outline }: { room: VenueRoom; label?: React.ReactNode; grid?: boolean; outline?: string }) {
  const indoor = room.kind === 'indoor';
  return (
    <div className="pointer-events-none" aria-hidden>
      {label && <div className="absolute flex items-baseline gap-2 whitespace-nowrap" style={{ left: room.x, top: room.y - 32 }}>{label}</div>}
      <div
        className={`absolute box-border ${indoor ? 'rounded-md' : 'rounded-[20px]'}`}
        style={{
          left: room.x, top: room.y, width: room.w, height: room.h,
          background: indoor ? C.room : C.court,
          border: indoor ? `${WALL}px solid ${C.wall}` : `2px dashed ${C.lineStrong}`,
          backgroundImage: indoor
            ? grid ? `radial-gradient(circle, ${C.lineStrong} 1.1px, transparent 1.4px)` : undefined
            : `repeating-linear-gradient(0deg, ${C.line} 0 1px, transparent 1px 30px), repeating-linear-gradient(90deg, ${C.line} 0 1px, transparent 1px 30px)`,
          backgroundSize: indoor && grid ? '20px 20px' : undefined,
          outline, outlineOffset: 8,
        }}
      />
      {room.openings.map(o => {
        const p = openingParts(room, o);
        return (
          <div key={o.id}>
            {p.gap && <div className="absolute" style={{ ...p.gap, background: C.outside }} />}
            {p.strip && (
              <div className="absolute box-border" style={{
                left: p.strip.left, top: p.strip.top, width: p.strip.width, height: p.strip.height, background: C.room,
                ...(p.strip.horiz
                  ? { borderTop: `1.5px solid ${C.wall}`, borderBottom: `1.5px solid ${C.wall}` }
                  : { borderLeft: `1.5px solid ${C.wall}`, borderRight: `1.5px solid ${C.wall}` }),
              }} />
            )}
            {p.leaves.map((l, i) => <div key={i} className="absolute box-border" style={leafStyle(l)} />)}
            {p.label && (
              <div className="absolute whitespace-nowrap text-[12px] text-t3"
                style={{ left: p.label.left, top: p.label.top, transform: p.label.vertical ? 'translateY(-50%)' : 'translateX(-50%)', writingMode: p.label.vertical ? 'vertical-rl' : undefined }}>
                {o.label}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const FIXTURE_LOOK: Record<Fixture['type'], string> = {
  bar: 'bg-surface-3 border-[1.5px] border-line-strong text-t3 rounded-[10px]',
  column: 'rounded-[4px]',
  plant: 'rounded-full bg-emerald-100 border-2 border-emerald-400 dark:bg-emerald-800/50 dark:border-emerald-500',
  label: 'text-t3 rounded-[10px]',
};

/** Σταθερό στοιχείο: μπαρ/πάγκος, κολώνα, φυτό, ετικέτα (σε μονάδες χώρου). */
export function fixtureStyle(f: Fixture): { className: string; style: React.CSSProperties } {
  return {
    className: `absolute box-border flex items-center justify-center ${FIXTURE_LOOK[f.type]}`,
    style: { left: f.x, top: f.y, width: f.w, height: f.h, ...(f.type === 'column' ? { background: C.wall } : null) },
  };
}

export function FixtureLabel({ f }: { f: Fixture }) {
  if (f.type !== 'bar' && f.type !== 'label') return null;
  const vertical = f.type === 'bar' && f.h > f.w;
  return (
    <span className={`whitespace-nowrap font-bold ${f.type === 'bar' ? 'text-[11px] tracking-[0.3em]' : 'text-[12.5px]'}`}
      style={vertical ? { writingMode: 'vertical-rl', transform: 'rotate(180deg)' } : undefined}>
      {f.label}
    </span>
  );
}
